<template>
    <div class="content w-full flex flex-col px-5 pt-16 justify-center pb-10">
        <div v-if="auth.isAuthenticated && auth.getUser.hasStudent() && auth.getUser.student.isAllowed()" class="mb-3">
            <div class="flex justify-end items-end mb-10">
                <Button label="Ver materiais exclusivos" icon="pi pi-arrow-right" iconPos="right" 
                    class="p-button-text font-bold text-carrot" 
                    @click="urlHandler.redirectAndScroll('/dashboard/materials')" />
            </div>
            <Divider />
        </div>

        <section class="w-full justify-center flex flex-wrap">
            <div v-if="!loadingCourses && !myCourses.original.length" class="w-2/3 mx-auto text-center">
                <p class="text-xl mb-5">
                    {{ translate("you don't have any courses yet!", 'ucfirst') }} 
                </p>
                <Button 
                    :label="translate('search courses', 'upper')"
                    class="no-bg-button"
                    @click="goToHomePage()"
                />
            </div>

            <template v-if="myCourses.original.length > 0">
                <div class="w-full md:w-3/4 lg:w-2/3 mx-auto" v-if="!loadingCourses">
                    <h1 class="text-2xl font-bold text-left mb-10">{{ translate('my courses', 'ucfirst') }}:</h1>
                </div>
                <div class="filters w-full md:w-3/4 lg:w-2/3 mx-auto flex flex-wrap md:flex-nowrap justify-between mb-10 gap-6" v-if="!loadingCourses">
                    <div class="w-full">
                        <p>{{ translate('filter by', 'ucfirst') }}</p>
                        <Dropdown 
                            v-model="selectedStatus" 
                            :options="getStatusOptions()" 
                            optionLabel="name" 
                            :placeholder="translate('completed', 'ucfirst')" 
                            class="w-full md:w-14rem" 
                            @change="filterCourses()"
                        />
                    </div>
                    <div class="w-full mt-auto">
                        <InputText 
                            class="w-5/6"
                            :placeholder="translate('course title', 'ucfirst')"
                            v-model="titleToSearch" 
                            style="border-top-right-radius: 0%; border-bottom-right-radius: 0%;" 
                        />
                        <Button 
                            :placeholder="translate('search menus', 'ucfirst')"
                            class="bg-button min-h-[40px] w-1/6"
                            icon="pi pi-search" 
                            aria-label="search" 
                            style="border-top-left-radius: 0%; border-bottom-left-radius: 0%;"
                            @click.prevent="searchCourseByTitle()" 
                        />
                    </div>
                </div>
            </template>
            <div v-if="loadingCourses" class="w-full text-center">
                <LoaderComponent />
            </div>
            
            <div class="w-full mx-5" v-if="!loadingCourses">
                <Carousel :value="myCourses.data" :numVisible="3" :numScroll="3" :responsiveOptions="responsiveOptions" v-if="myCourses.data.length">
                    <template #item="slotProps">
                        <div 
                            class="shadow border border-iron rounded m-2 p-4 max-w-20 mx-10 cursor-pointer" 
                            :class="selectedCard == slotProps.data.uuid ? 'selected-card' : ''"
                        >
                            <div @click.prevent="visitCoursePage(slotProps.data.uuid)">
                                <div class="mb-4">
                                    <Tag v-if="slotProps.data.isBlocked()" severity="danger" class="absolute z-10" style="margin-top: 0.3rem; margin-left: 0.3rem;">
                                        {{ translate('expired', 'upper') }}
                                    </Tag>
                                    <CoursePhotoComponent 
                                        :course="slotProps.data" 
                                        :class="slotProps.data.isBlocked() ? 'opacity-50' : ''"
                                    />
                                </div>
                                <div class="mb-4 font-medium">{{ slotProps.data.title }}</div>
                            </div>
                            
                            <Button 
                                v-if="slotProps.data.isBlocked()"
                                class="mt-5 w-full bg-purple-suit-forced text-white p-1"
                                :label="translate('rebuy course', 'ucfirst')"
                                @click.prevent="attemptCourseBuying(slotProps.data)"
                            />
                        </div>
                    </template>
                </Carousel>
            </div>
        </section>

        <section class="w-2/3 mx-auto mt-10 pt-10">
            <h1 class="text-2xl font-bold text-left mb-10" v-if="!loadingCourses && myCourses.data.length > 0">
                {{ translate('my performance', 'ucfirst') }}:
            </h1>
            <div v-if="loadingCourses" class="w-full text-center">
                <LoaderComponent />
            </div>
            <div class="flex justify-center" v-if="!loadingCourses && myCourses.data.length > 0">
                <Chart type="pie" :data="chartData" :options="chartOptions" class="w-full md:w-[20rem]" />
            </div>
        </section>

        <BuyCourseModalComponent v-model:visible="showBuyCourse" :course="selectedCourse" />
    </div>
</template>
<script setup>
    import { onMounted, ref, reactive, onUnmounted } from 'vue';
    import { showToast } from '@/composables/toast';
    import { useToast } from 'primevue/usetoast';
    import Carousel from 'primevue/carousel';
    import LoaderComponent from '@/components/Utils/LoaderComponent.vue';
    import InputText from 'primevue/inputtext';
    import Dropdown from 'primevue/dropdown';
    import Button from 'primevue/button';
    import { sleep } from '@/composables/sleep';
    import Chart from 'primevue/chart';
    import courseService from '@/services/CourseService';
    import CoursePhotoComponent from '@/components/Course/CoursePhotoComponent.vue';
    import { useAuthStore } from '@/stores/auth';
    import { Divider } from 'primevue';
    import Tag from 'primevue/tag';
    import urlHandler from '@/composables/urlHandler';
    import { translate } from '@/composables/translate';
    import BuyCourseModalComponent from '@/components/Payment/BuyCourseModalComponent.vue';
    
    const toast = useToast();
    const auth = useAuthStore();
    const chartData = ref();
    const chartOptions = ref();

    const showBuyCourse = ref(false);
    const selectedCourse = ref(null);

    onMounted(() => {
        loadCourseAsync();
    })

    onUnmounted(() => {
        myCourses.loadingAsync = false;
        myCourses.stopAsync = true;
    })

    let titleToSearch = ref('');
    
    function getStatusOptions() {
        return [
            { code: '', name: translate('both', 'ucfirst') },
            { code: 'incomplete', name: translate('in progress', 'ucfirst') },
            { code: 'complete', name: translate('Completed', 'ucfirst') }
        ]; 
    }

    let selectedStatus = ref({ code: '', name: translate('both', 'ucfirst') });
    let loadingCourses = ref(true);
    let myCourses = reactive({
        total: 0,
        data: [],
        original: [],
        byUuid: {},
        byName: {},
        byStatus: {
            complete: [],
            incomplete: []
        },
        loadingAsync: true,
        stopAsync: false,
        asyncFirstLoad: false
    });
    async function getMyCourses(){
        let response = myCourses.data;
        response = await courseService.getLoggedStudentCourses();

        myCourses.byUuid = response.getContent().byUuid;
        myCourses.byName = response.getContent().byName;
        myCourses.byStatus = response.getContent().byStatus;
        if(!response.success()){
            myCourses.total = 0;
            myCourses.data  = [];
            showToast(toast, translate('a problem occurred'), 'error');
            return;
        }
        myCourses.total    = response.getContent().courses.length;
        myCourses.data     = response.getContent().courses;
        myCourses.original = response.getContent().courses;

        chartData.value = setChartData();
        chartOptions.value = setChartOptions();
    }

    /**
     * Calls getMyCourses() in a loop
     */
    async function loadCourseAsync() {
        let performed = false;
        while (myCourses.loadingAsync) {
            if (performed) {
                await sleep(30000);
            } else {
                performed = true;
            }

            if (myCourses.stopAsync) {
                break;
            }

            if (!myCourses.asyncFirstLoad) {
                loadingCourses.value = true;
            }

            getMyCourses();

            if (!myCourses.asyncFirstLoad) {
                loadingCourses.value = false;
                myCourses.asyncFirstLoad = true;
            }
        }
    }

    async function searchCourseByTitle(){
        if(titleToSearch.value == ''){
            myCourses.data = myCourses.original;
        }else{
            let matches = [];
            for(let course of myCourses.data){
                const courseTitle = course.title.toLocaleLowerCase();
                if(courseTitle.indexOf(titleToSearch.value) != -1)
                    matches.push(course);
            }
            loadingCourses.value = true;
            await sleep(100);
            loadingCourses.value = false;
            myCourses.data = matches;
        }
    }

    async function filterCourses(){
        loadingCourses.value = true;
        await sleep(100);
        loadingCourses.value = false;
        if(selectedStatus.value.code == ''){
            myCourses.data = myCourses.original;
            return;
        }
        myCourses.data = myCourses.byStatus[selectedStatus.value.code];
    }

    const responsiveOptions = ref([
        {
            breakpoint: '1400px',
            numVisible: 2,
            numScroll: 1
        },
        {
            breakpoint: '1199px',
            numVisible: 3,
            numScroll: 1
        },
        {
            breakpoint: '767px',
            numVisible: 2,
            numScroll: 1
        },
        {
            breakpoint: '575px',
            numVisible: 1,
            numScroll: 1
        }
    ]);

    const setChartData = () => {
        const documentStyle = getComputedStyle(document.body);
        return {
            labels: [translate('completed'), translate('in progress')],
            datasets: [
                {
                    data: [myCourses.byStatus.complete.length, myCourses.byStatus.incomplete.length],
                    backgroundColor: [documentStyle.getPropertyValue('--p-cyan-500'), documentStyle.getPropertyValue('--p-orange-500')],
                    hoverBackgroundColor: [documentStyle.getPropertyValue('--p-cyan-400'), documentStyle.getPropertyValue('--p-orange-400')]
                }
            ]
        };
    };

    const setChartOptions = () => {
        const documentStyle = getComputedStyle(document.documentElement);
        const textColor = documentStyle.getPropertyValue('--p-text-color');
        return {
            plugins: {
                legend: {
                    labels: {
                        usePointStyle: true,
                        color: textColor
                    }
                }
            }
        };
    };

    async function visitCoursePage(uuid = ''){
        const course = myCourses.byUuid[uuid];

        if (course.isBlocked()) {
            showToast(toast, translate('the course access expired, you must buy again to regain access', 'ucfirst'), 'error');
            return;
        }

        urlHandler.redirectAndScroll(`/dashboard/course/${uuid}`);
    }

    function goToHomePage(){
        urlHandler.redirectAndScroll('/');
    }

    /**
     * Opens BuyCourseModal
     * @param course 
     */
    function attemptCourseBuying(course = null) {
        selectedCourse.value = course;
        showBuyCourse.value = true;
    }
</script>